import React, { useState } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { Text } from '@nextiva/next-ui';
import { CreditCardForm } from '@nextiva/credit-card-form';
import { RadioGroup, FormControlLabel, Radio } from '@material-ui/core';

import { Select, TextField } from '@nextiva/next-ui-admin';

import { supportedCards, accountTypes, paymentOptions } from 'utils';
import './PaymentMethodPicker.scss';
import GenericSpinner from 'UIElements/GenericSpinner/GenericSpinner';
import { useDefaultPaymentProvider } from 'hooks/usePaymentProvider/usePaymentProvider';
import { getPaymentProviderProps } from 'getters/paymentProviderGetters/paymentProviderGetters';

export const blockName = 'payment-method-picker';

const PaymentMethodPicker = ({
  paymentOption,
  routingNumber,
  validateRoutingNumber,
  accountNumber,
  bankName,
  accountType,
  configDrawer,
  onVantivActionsLoad,
  handleRoutingNumberChange,
  handleAccountNumberChange,
  handlePaymentOptionChange,
  validateNumbersOnly,
  handleBankNameChange,
  validateTextOnly,
  handleAccountTypeChange,
}) => {
  const [vantivProps, setVantivProps] = useState(configDrawer);

  const {
    isLoading: isLoadingProvider,
    isFetching: isFetchingProvider,
  } = useDefaultPaymentProvider({
    onError: () => {},
    onSuccess: argPaymentProvider => {
      const parsedProps = getPaymentProviderProps(argPaymentProvider);
      setVantivProps(prev => ({ ...prev, ...parsedProps }));
    },
  });

  return (
    <div data-testid={blockName} className={blockName}>
      <p className={`${blockName}__options`}>PAYMENT TYPE</p>
      <RadioGroup
        row
        id="payment"
        aria-label="Payment type"
        name="payment"
        value={paymentOption}
        onChange={handlePaymentOptionChange}
      >
        <FormControlLabel
          value={paymentOptions.CREDIT_CARD}
          control={<Radio />}
          label="Credit Card"
        />
        <FormControlLabel
          value={paymentOptions.ECHECK}
          disabled={true}
          control={<Radio />}
          label="eCheck"
        />
      </RadioGroup>
      <div
        className={clsx(
          paymentOption === paymentOptions.CREDIT_CARD
            ? `${blockName}--visible`
            : `${blockName}--hidden`
        )}
      >
        {isLoadingProvider || isFetchingProvider ? (
          <GenericSpinner />
        ) : (
          <CreditCardForm
            className={`${blockName}__card-form`}
            data-testid="BillingLicensing-PaymentMethodPicker-CreditCardForm"
            scriptSrc={vantivProps.iframeSrc}
            config={vantivProps}
            onLoad={onVantivActionsLoad}
          />
        )}
        <div className={`${blockName}__card-types`}>
          {window.eprotectIframeClient && (
            <div className={`${blockName}__supported-cc`}>
              {supportedCards.map(cardType => (
                <i
                  className={`${blockName}__supported-cc__credit-card ${blockName}__supported-cc__credit-card--${cardType}`}
                  key={cardType}
                />
              ))}
            </div>
          )}
        </div>
      </div>
      <div
        className={clsx(
          paymentOption === paymentOptions.ECHECK
            ? `${blockName}--visible`
            : `${blockName}--hidden`,
          `${blockName}__echeck`
        )}
      >
        <Text kind="h4">eCheck</Text>
        <div className={`${blockName}__horizontal-fields`}>
          <TextField
            className={`${blockName}__field-adjust`}
            id="routing-number"
            touchValidation
            label="Routing number"
            placeholder="XXXXXXXXXX"
            value={routingNumber}
            onChange={handleRoutingNumberChange}
            errorMessage="Insert Valid Routing Number"
            validate={validateRoutingNumber}
          />
          <TextField
            className={`${blockName}__field-adjust`}
            id="account-number"
            touchValidation
            value={accountNumber}
            onChange={handleAccountNumberChange}
            errorMessage="Insert Account Number"
            label="Account number"
            placeholder="XXXXXXXXXX"
            validate={validateNumbersOnly}
          />
        </div>
        <div
          className={`${blockName}__horizontal-fields ${blockName}__horizontal-fields--pading-top ${blockName}__horizontal-fields--border-top ${blockName}--top-space`}
        >
          <TextField
            className={`${blockName}__field-adjust`}
            id="bank-name"
            touchValidation
            label="Bank Name"
            value={bankName}
            onChange={handleBankNameChange}
            errorMessage="Insert Bank Name"
            placeholder="Bank Name"
            validate={validateTextOnly}
          />
          <Select
            id="account-type"
            touchValidation
            label="Account type"
            placeholder="Account type"
            options={accountTypes}
            value={accountType}
            onChange={handleAccountTypeChange}
            className={`${blockName}__field-adjust`}
            validate={validateTextOnly}
          />
        </div>
      </div>
    </div>
  );
};

PaymentMethodPicker.propTypes = {
  paymentOption: PropTypes.string.isRequired,
  routingNumber: PropTypes.string.isRequired,
  accountNumber: PropTypes.string.isRequired,
  bankName: PropTypes.string.isRequired,
  accountType: PropTypes.string.isRequired,
  configDrawer: PropTypes.object.isRequired,
  onVantivActionsLoad: PropTypes.func.isRequired,
  validateRoutingNumber: PropTypes.func.isRequired,
  handleRoutingNumberChange: PropTypes.func.isRequired,
  handleAccountNumberChange: PropTypes.func.isRequired,
  handlePaymentOptionChange: PropTypes.func.isRequired,
  validateNumbersOnly: PropTypes.func.isRequired,
  handleBankNameChange: PropTypes.func.isRequired,
  validateTextOnly: PropTypes.func.isRequired,
  handleAccountTypeChange: PropTypes.func.isRequired,
};

export default PaymentMethodPicker;
